import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { blogsAPI } from '../services/api';
import { ArrowLeft, Calendar, Clock, User } from 'lucide-react';
import { Button } from '../components/ui/button';

const BlogDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedBlogs, setRelatedBlogs] = useState([]);

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        const response = await blogsAPI.getById(id);
        setBlog(response.data);
        
        // Fetch related blogs
        const allBlogs = await blogsAPI.getAll({ published: true });
        const related = allBlogs.data
          .filter(b => b.id !== id && b.category === response.data.category)
          .slice(0, 3);
        setRelatedBlogs(related);
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching blog:', error);
        setLoading(false);
      }
    };
    
    fetchBlog();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-xl">Loading article...</div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl text-white mb-4">Article not found</h1>
          <Button onClick={() => navigate('/blog')} className="bg-red-700 hover:bg-red-800">
            Back to Blog
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section with Image */}
      <div className="relative bg-gradient-to-br from-neutral-900 to-black border-b border-gray-800 overflow-hidden">
        {/* Background Image */}
        {blog.imageUrl && (
          <>
            <div className="absolute inset-0">
              <img 
                src={blog.imageUrl} 
                alt={blog.title}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/85 to-black/60"></div>
          </>
        )}
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 py-12">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/blog')}
            className="text-gray-400 hover:text-white mb-8 bg-black/30 backdrop-blur-sm"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Blog
          </Button>
          
          <div className="mb-6">
            <span className="inline-block text-red-500 font-semibold text-sm uppercase tracking-wider bg-red-500/10 px-4 py-2 rounded-full border border-red-500/20 backdrop-blur-sm">
              {blog.category || 'Article'}
            </span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight drop-shadow-lg">
            {blog.title}
          </h1>
          
          <div className="flex flex-wrap items-center gap-6 text-gray-300">
            <span className="flex items-center">
              <User className="h-5 w-5 mr-2 text-red-500" />
              {blog.author || 'Dr. Rocco de Filippis'}
            </span>
            <span className="flex items-center">
              <Calendar className="h-5 w-5 mr-2 text-red-500" />
              {new Date(blog.publishedDate || blog.createdAt).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </span>
            {blog.readTime && (
              <span className="flex items-center">
                <Clock className="h-5 w-5 mr-2 text-red-500" />
                {blog.readTime}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Article Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <article className="prose prose-invert prose-lg max-w-none">
          <div className="text-gray-300 leading-relaxed whitespace-pre-line">
            {blog.content}
          </div>
        </article>

        {/* Related Articles */}
        {relatedBlogs.length > 0 && (
          <div className="mt-16 pt-12 border-t border-gray-800">
            <h2 className="text-2xl font-bold text-white mb-8">Related Articles</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {relatedBlogs.map((relatedBlog) => (
                <div
                  key={relatedBlog.id}
                  onClick={() => navigate(`/blog/${relatedBlog.id}`)}
                  className="bg-neutral-900 border border-gray-800 rounded-lg p-6 hover:border-red-900 transition-all cursor-pointer group"
                >
                  <div className="text-red-500 text-sm font-semibold mb-2">
                    {relatedBlog.category}
                  </div>
                  <h3 className="text-white font-bold mb-2 group-hover:text-red-500 transition-colors line-clamp-2">
                    {relatedBlog.title}
                  </h3>
                  <p className="text-gray-400 text-sm line-clamp-2">
                    {relatedBlog.excerpt}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogDetail;
